import { useEffect, useState, useCallback } from 'react';
import { X, Check } from 'lucide-react';
import fetchWithTenant from '../utils/fetchWithTenant';

// CRITICAL: Use <div> NOT <label> for checklist items — labels fire onClick
// twice, causing a toggle/untoggle race.

const TITLES = {
  cameras: 'Camera Widget Settings',
  doors:   'Doors Widget Settings',
  alerts:  'Alerts Widget Settings',
  devices: 'Devices Widget Settings',
};

export default function WidgetSettingsModal({ widget, onSave, onClose }) {
  const [draft, setDraft] = useState(widget.settings || {});

  // Escape key to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const handleSave = () => onSave(draft);

  const renderBody = () => {
    switch (widget.id) {
      case 'cameras':  return <CamerasSettings draft={draft} setDraft={setDraft} />;
      case 'doors':    return <DoorsSettings draft={draft} setDraft={setDraft} />;
      case 'alerts':   return <AlertsSettings draft={draft} setDraft={setDraft} />;
      case 'devices':  return <DevicesSettings draft={draft} setDraft={setDraft} />;
      default:         return <div className="wsm-empty">No settings available for this widget.</div>;
    }
  };

  return (
    <div className="wsm-overlay" onClick={onClose}>
      <div className="wsm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="wsm-header">
          <h3 className="wsm-title">{TITLES[widget.id] || 'Widget Settings'}</h3>
          <button className="wsm-close" onClick={onClose} aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="wsm-body">
          {renderBody()}
        </div>
        <div className="wsm-footer">
          <button className="wsm-btn wsm-btn-ghost" onClick={onClose}>Cancel</button>
          <button className="wsm-btn wsm-btn-primary" onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  );
}

// ── Cameras: select up to 6 ───────────────────────────────────
function CamerasSettings({ draft, setDraft }) {
  const [cameras, setCameras] = useState(null);
  const [error, setError] = useState(false);
  const selected = draft.cameraIds || [];
  const MAX = 6;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // CRITICAL: fetch from /api/vms/cameras directly (NOT useStore().cameras)
        const r = await fetchWithTenant('/api/vms/cameras');
        if (!r.ok) throw new Error('fetch failed');
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d) ? d : (d.cameras || []);
        setCameras(list);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = useCallback((id) => {
    setDraft((d) => {
      const cur = d.cameraIds || [];
      if (cur.includes(id)) {
        return { ...d, cameraIds: cur.filter(x => x !== id) };
      }
      if (cur.length >= MAX) return d;
      return { ...d, cameraIds: [...cur, id] };
    });
  }, [setDraft]);

  if (error) return <div className="wsm-error">Failed to load cameras</div>;
  if (!cameras) return <div className="wsm-loading">Loading…</div>;
  if (cameras.length === 0) return <div className="wsm-empty">No cameras available</div>;

  return (
    <>
      <div className="wsm-hint">
        Select up to {MAX} cameras to display ({selected.length}/{MAX} selected)
      </div>
      <div className="wsm-checklist">
        {cameras.map(c => {
          const isSelected = selected.includes(c.id);
          const disabled = !isSelected && selected.length >= MAX;
          return (
            <div
              key={c.id}
              className={`wsm-check-item ${isSelected ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
              onClick={() => !disabled && toggle(c.id)}
            >
              <span className={`wsm-check-box ${isSelected ? 'checked' : ''}`}>
                {isSelected && <Check size={12} />}
              </span>
              <span className="wsm-check-label">{c.name || c.id}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Doors ─────────────────────────────────────────────────────
function DoorsSettings({ draft, setDraft }) {
  const [doors, setDoors] = useState(null);
  const [error, setError] = useState(false);
  const selected = draft.doorIds || [];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchWithTenant('/api/acs/doors');
        if (!r.ok) throw new Error('fetch failed');
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d) ? d : (d.doors || d.readers || []);
        setDoors(list);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = useCallback((id) => {
    setDraft((d) => {
      const cur = d.doorIds || [];
      return { ...d, doorIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  }, [setDraft]);

  if (error) return <div className="wsm-error">Failed to load doors</div>;
  if (!doors) return <div className="wsm-loading">Loading…</div>;
  if (doors.length === 0) return <div className="wsm-empty">No doors available</div>;

  return (
    <>
      <div className="wsm-hint">Select doors to monitor ({selected.length} selected, all if none selected)</div>
      <div className="wsm-checklist">
        {doors.map(d => {
          const id = d.id || d.readerId;
          const isSelected = selected.includes(id);
          return (
            <div
              key={id}
              className={`wsm-check-item ${isSelected ? 'selected' : ''}`}
              onClick={() => toggle(id)}
            >
              <span className={`wsm-check-box ${isSelected ? 'checked' : ''}`}>
                {isSelected && <Check size={12} />}
              </span>
              <span className="wsm-check-label">{d.name || d.readerName || id}</span>
            </div>
          );
        })}
      </div>
    </>
  );
}

// ── Alerts ────────────────────────────────────────────────────
function AlertsSettings({ draft, setDraft }) {
  const SEVERITIES = [
    { id: 'critical', label: 'Critical' },
    { id: 'warning',  label: 'Warning' },
    { id: 'info',     label: 'Info' },
  ];
  const selected = draft.severities || [];
  const limit = draft.limit || 5;

  const toggleSev = (id) => {
    setDraft((d) => {
      const cur = d.severities || [];
      return { ...d, severities: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  };

  return (
    <>
      <div className="wsm-section">
        <div className="wsm-section-label">Severity Filter</div>
        <div className="wsm-chips">
          {SEVERITIES.map(s => {
            const isSelected = selected.includes(s.id);
            return (
              <div
                key={s.id}
                className={`wsm-chip sev-${s.id} ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleSev(s.id)}
              >
                {s.label}
              </div>
            );
          })}
        </div>
        <div className="wsm-hint">Empty = show all severities</div>
      </div>
      <div className="wsm-section">
        <div className="wsm-section-label">Show how many alerts</div>
        <select
          className="wsm-select"
          value={limit}
          onChange={(e) => setDraft((d) => ({ ...d, limit: Number(e.target.value) }))}
        >
          <option value={3}>3</option>
          <option value={5}>5</option>
          <option value={10}>10</option>
        </select>
      </div>
    </>
  );
}

// ── Devices ───────────────────────────────────────────────────
function DevicesSettings({ draft, setDraft }) {
  const [devices, setDevices] = useState(null);
  const [error, setError] = useState(false);
  const selectedIds = draft.deviceIds || [];
  const filter = draft.filter || 'all';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchWithTenant('/api/md/devices');
        if (!r.ok) throw new Error('fetch failed');
        const d = await r.json();
        if (cancelled) return;
        const list = Array.isArray(d) ? d : (d.devices || []);
        setDevices(list);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggle = (id) => {
    setDraft((d) => {
      const cur = d.deviceIds || [];
      return { ...d, deviceIds: cur.includes(id) ? cur.filter(x => x !== id) : [...cur, id] };
    });
  };

  return (
    <>
      <div className="wsm-section">
        <div className="wsm-section-label">Status Filter</div>
        <div className="wsm-chips">
          {['all', 'online', 'issues'].map(f => (
            <div
              key={f}
              className={`wsm-chip ${filter === f ? 'selected' : ''}`}
              onClick={() => setDraft((d) => ({ ...d, filter: f }))}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </div>
          ))}
        </div>
      </div>
      <div className="wsm-section">
        <div className="wsm-section-label">Devices ({selectedIds.length} selected, all if none)</div>
        {error ? <div className="wsm-error">Failed to load devices</div>
         : !devices ? <div className="wsm-loading">Loading…</div>
         : devices.length === 0 ? <div className="wsm-empty">No devices</div>
         : (
          <div className="wsm-checklist">
            {devices.map(d => {
              const isSelected = selectedIds.includes(d.id);
              return (
                <div
                  key={d.id}
                  className={`wsm-check-item ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggle(d.id)}
                >
                  <span className={`wsm-check-box ${isSelected ? 'checked' : ''}`}>
                    {isSelected && <Check size={12} />}
                  </span>
                  <span className="wsm-check-label">{d.name || d.hostname || d.id}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
